import {Box, Typography} from '@mui/material';
import {FC} from 'react';

import {CAPTION, H6} from '@/utils/constants';
import {adminSmallBlockStyle} from './AdminSmallBlock.style';
import {ReactComponent as TrendUp} from '@icons/trend-up.svg';
import {ReactComponent as TrendDown} from '@icons/trend-down.svg';

import {ReactComponent as LoginIcon} from '@icons/login.svg';
import {ReactComponent as NoteText} from '@icons/note-text.svg';
import {ReactComponent as ProfileIcon} from '@icons/profile-blue.svg';
import {ReactComponent as DollarIcon} from '@icons/dollar-square.svg';
import {IAdminStatisticResponse} from '@/utils/types/superAdmin.interface';
import {currencyFormatter} from '@/utils/helpers/currency';

interface IAdminSmallBlock {
  statistics: IAdminStatisticResponse | null;
}

const COMPACT_FROM = 1_000_000;

const fullNumber = new Intl.NumberFormat('en-US');
const compactNumber = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1,
});

const compactCurrency = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumFractionDigits: 1,
});

const formatCount = (v: number) => (v >= COMPACT_FROM ? compactNumber.format(v) : fullNumber.format(v));
const formatMoney = (v: number) => (v >= COMPACT_FROM ? compactCurrency.format(v) : currencyFormatter.format(v));

const AdminSmallBlock: FC<IAdminSmallBlock> = ({statistics}) => {
  const icons = [
    <ProfileIcon key="profile" />,
    <DollarIcon key="dollar" />,
    <LoginIcon key="login" />,
    <NoteText key="note" />,
  ];

  const customers = statistics?.totalCustomers || 0;
  const revenue = statistics?.totalRevenue || 0;
  const visits = statistics?.visitPerDay || 0;
  const events = statistics?.events || 0;

  const stats = [
    {
      label: 'Total customers',
      value: formatCount(customers),
      fullValue: fullNumber.format(customers),
      change: statistics?.totalCustomersChangePercent || 0,
    },
    {
      label: 'Total revenue',
      value: formatMoney(revenue),
      fullValue: currencyFormatter.format(revenue),
      change: statistics?.totalRevenueChangePercent || 0,
    },
    {
      label: 'Visits per day',
      value: formatCount(visits),
      fullValue: fullNumber.format(visits),
      change: statistics?.visitPerDayChangePercent || 0,
    },
    {
      label: 'Events',
      value: formatCount(events),
      fullValue: fullNumber.format(events),
      change: statistics?.eventsChangePercent || 0,
    },
  ];

  return (
    <Box sx={adminSmallBlockStyle.wrapper}>
      {stats.map((item, index) => (
        <Box key={item.label} sx={adminSmallBlockStyle.item}>
          <Box sx={adminSmallBlockStyle.iconBlock}>{icons[index]}</Box>

          <Box sx={adminSmallBlockStyle.content}>
            <Box sx={adminSmallBlockStyle.topBlock}>
              <Typography variant={H6} title={item.fullValue} sx={adminSmallBlockStyle.data}>
                {item.value}
              </Typography>

              <Box sx={adminSmallBlockStyle.changeWrapper}>
                {item.change > 0 ? <TrendUp /> : <TrendDown />}
                <Typography
                  sx={{
                    ...adminSmallBlockStyle.percents,
                    color: item.change > 0 ? 'var(--success-500)' : 'var(--error-500)',
                  }}
                >
                  {Math.abs(item.change).toFixed(1)}%
                </Typography>
              </Box>
            </Box>

            <Typography noWrap sx={adminSmallBlockStyle.name}>
              {item.label}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default AdminSmallBlock;
